/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { useDisplayRfFeedLatestData } from "./hooks";
import { useStoreDispatch } from "../../../store/hooks";
import "../Config.css";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { useSelector } from "react-redux";
import { customDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { IRfFeed } from "../../../store/api/deviceConfig/types";
import { setOpenCustomDailog, setConfirmDialog } from "../../../store/helpers/helperReducers";
import { setIsAddNewRfFeed, setSelectedRfFeed } from "../../../store/api/deviceConfig/reducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { constStr } from "../../../globals/constants";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import AddEditRfFeedLatest from "../../../components/Configuration/RfFeedLatest/AddEditRfFeedLatest";

const RfFeedsLatest: React.FC = () => {
  const dispatch = useStoreDispatch();
  const { data, isLoading, permissions, RfFeedsColumns, selectedRfFeed, deleteRfFeedHandler } = useDisplayRfFeedLatestData();

  //const getDataFromApi = data?.data || [];
  const rowsData = data || [];
  const [NoOfSelectedRow, setNoOfSelectedRow] = React.useState(0);
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");
  const [selectedRowIDs, setSelectedRowIDs] = React.useState<GridRowSelectionModel>([]);

  const openCustomDialog = useSelector(customDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const handleOnAddRfFeed = () => {
    handleClearSelection();
    setModelOpenMode("Add");
    dispatch(setIsAddNewRfFeed(true));
    dispatch(setOpenCustomDailog(true));
  };

  const handleOnEdit = () => {
    setModelOpenMode("Edit");
    dispatch(setIsAddNewRfFeed(false));
    dispatch(setOpenCustomDailog(true));
  };

  const handleClearSelection = () => {
    setSelectedRowIDs([]);
    setNoOfSelectedRow(0);
  };

  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    setSelectedRowIDs(rowIds);
    const rowIdLength = rowIds?.length;
    setNoOfSelectedRow(rowIdLength);
    if (rowIdLength === 1) {
      dispatch(setSelectedRfFeed(rowsData.filter((item) => item.index === rowIds[0])[0]));
    }
  };

  const handleOnDelete = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.RFFEED));
  };

  return (
    <>
      {openCustomDialog && <AddEditRfFeedLatest modelOpenMode={modelOpenMode} selectedRfFeed={selectedRfFeed} />}
      {openConfirmDialog === DIALOGOPENFOR.RFFEED && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteRfFeedHandler}>
          {`Are you sure you want to delete RF FEED ${selectedRfFeed.index}?`}
        </ConfirmDialog>
      )}
      <div
        style={{
          minHeight: "300px",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "18px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--card)",
        }}
      >
        {isLoading && <p>Loading...</p>}
        {!isLoading && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                {NoOfSelectedRow > 0 ? (
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">{`${NoOfSelectedRow} selected`}</p>
                    {permissions?.hasOpCodeGroup("manage_topology") && NoOfSelectedRow === 1 && (
                      <>
                        <Button variant="secondary" size="sm" onClick={handleOnEdit}>
                          Edit
                        </Button>
                        <Button variant="secondary" size="sm" onClick={handleOnDelete}>
                          <Icon name="delete" size={16} />
                          Delete
                        </Button>
                      </>
                    )}
                    <Button variant="secondary" size="sm" onClick={handleClearSelection}>
                      Clear
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{`Showing ${rowsData.length} results`}</p>
                )}
              </div>
              {permissions?.hasOpCodeGroup("manage_topology") && (
                <Button
                  className="h-10 px-4 text-sm font-medium border border-input bg-background shadow-sm flex items-center gap-2 hover:bg-muted hover:text-accent-foreground transition-colors"
                  variant="outline"
                  onClick={handleOnAddRfFeed}
                >
                  <Icon name="add" size={18} />
                  Add RF Feed
                </Button>
              )}
            </div>
            <CustomDataGrid
              tableId="rfFeedLatest"
              rows={rowsData}
              columns={RfFeedsColumns}
              checkboxSelection={true}
              loading={isLoading}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              onRowSelectionModelChange={handleOnRowSelected}
              rowSelectionModel={selectedRowIDs}
              getRowId={(row: IRfFeed) => row.index!}
              sx={{
                "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
                "& .MuiDataGrid-scrollbar--vertical": { display: "none" },
              }}
              fillContainer={false}
            />
          </div>
        )}
      </div>
    </>
  );
};
export default RfFeedsLatest;
